import { Button } from "antd";
import {
  CloseOutlined,
  CompressOutlined,
  HolderOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import type { ResponsiveLayouts } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import type {
  DashboardBreakpoint,
  PlotLayoutsByBreakpoint,
} from "@/types/tableState.types";
import { VivoPlotWrapper } from "@/components/VivoPlots/wrapper";
import PlotPlaceholder from "../UI/PlotPlaceholder";
import type { DashboardGridProps, DragHandleWidgetProps } from "./types";
import {
  GRID_BREAKPOINTS,
  GRID_COLUMNS,
  GRID_MARGIN,
  GRID_ROW_HEIGHT,
  getPlotId,
} from "./dashboardLayout";

const ResponsiveDashboardGrid = WidthProvider(Responsive);

const DragHandleWidget = ({ title, type }: DragHandleWidgetProps) => (
  <div className="dashboard-widget-drag-handle flex h-full cursor-grab select-none flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border-2 border-dashed border-drag-border bg-drag-surface px-3 active:cursor-grabbing">
    <HolderOutlined className="text-xl text-drag-accent" />
    <p className="w-full truncate text-center text-sm font-semibold text-brand-dark">
      {title || "Untitled plot"}
    </p>
    <p className="text-xs capitalize text-widget-meta">
      {type?.replace("-", " ")}
    </p>
  </div>
);

export const DashboardGrid = ({
  visiblePlots,
  activeLayouts,
  isLayoutEditMode,
  onLayoutChange,
  onCancelEdit,
  onSaveEdit,
  onCompactLayout,
  onCreatePlot,
  globalFilters,
  columns,
  endpointSuffix,
  endpointPrefix,
  editPlot,
  hidePlot,
  deletePlot,
  duplicatePlot,
  onConfigChange,
  selectedIdName,
  selectedIdValue,
  setSelectedId,
  onSelectPlotElement,
  filterableColumns,
  appliedFilters,
  onPointActivate,
  metadata,
  columnsTitleCreator,
  datasetName,
  subjectId,
}: DashboardGridProps) => {
  if (visiblePlots.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <PlotPlaceholder onCreatePlot={onCreatePlot} />
      </div>
    );
  }

  return (
    <>
      {isLayoutEditMode && (
        <div className="mb-2 flex items-center justify-end gap-2 px-2">
          <Button onClick={onCompactLayout} icon={<CompressOutlined />}>
            Compact layout
          </Button>
          <Button onClick={onCancelEdit} icon={<CloseOutlined />}>
            Cancel
          </Button>
          <Button type="primary" onClick={onSaveEdit} icon={<SaveOutlined />}>
            Save changes
          </Button>
        </div>
      )}
      <div className="min-h-[400px] w-full px-2 py-2">
        <ResponsiveDashboardGrid
          key={isLayoutEditMode ? "edit" : "view"}
          measureBeforeMount
          layouts={activeLayouts as ResponsiveLayouts<DashboardBreakpoint>}
          breakpoints={GRID_BREAKPOINTS}
          cols={GRID_COLUMNS}
          rowHeight={GRID_ROW_HEIGHT}
          margin={GRID_MARGIN}
          compactType="vertical"
          preventCollision={false}
          allowOverlap={false}
          isDraggable={isLayoutEditMode}
          isResizable={isLayoutEditMode}
          resizeHandles={["se", "sw", "ne", "nw"]}
          useCSSTransforms
          className={
            isLayoutEditMode
              ? "dashboard-layout-grid is-editing"
              : "dashboard-layout-grid"
          }
          onLayoutChange={
            isLayoutEditMode
              ? (_layout, allLayouts) =>
                  onLayoutChange(allLayouts as PlotLayoutsByBreakpoint)
              : undefined
          }
        >
          {visiblePlots.map((plot, index) => (
            <div
              key={getPlotId(plot, index)}
              data-testid={`dashboard-layout-widget-${getPlotId(plot, index)}`}
              className="h-full"
            >
              {isLayoutEditMode ? (
                <DragHandleWidget
                  title={plot.config.labels?.title}
                  type={plot.type}
                />
              ) : (
                <VivoPlotWrapper
                  globalFilters={globalFilters}
                  columns={columns}
                  endpointSuffix={endpointSuffix}
                  endpointPrefix={endpointPrefix}
                  {...plot}
                  editPlot={editPlot}
                  selectedIdName={selectedIdName}
                  selectedIdValue={selectedIdValue}
                  setSelectedId={setSelectedId}
                  onElementSelect={onSelectPlotElement}
                  filterableColumns={filterableColumns}
                  appliedFilters={appliedFilters}
                  onPointActivate={onPointActivate}
                  hidePlot={hidePlot}
                  deletePlot={deletePlot}
                  duplicatePlot={duplicatePlot}
                  onConfigChange={onConfigChange}
                  metadata={metadata}
                  columnsTitleCreator={columnsTitleCreator}
                  datasetName={datasetName}
                  subjectId={subjectId}
                />
              )}
            </div>
          ))}
        </ResponsiveDashboardGrid>
      </div>
    </>
  );
};
